import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { forwardRef } from 'react'

import { DateInput, DateInputProps } from '@mantine/dates'
import classes from './datefield.module.css'

dayjs.extend(utc)
dayjs.extend(timezone)

function resetTime(date: dayjs.Dayjs) {
  return date
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)
}
const dateParser: DateInputProps['dateParser'] = (input) => {
  // if input is empty, return undefined
  if (!input) return null

  // remove all whitespace
  // replace all '/' with '' (to handle both DD/MM/YYYY and DDMMYYYY formats)
  input = input.replaceAll(/\s/g, '').replaceAll(/\//g, '')

  // if input is digit one or two digit, return date with current month and year
  if (/^\d{1,2}$/.test(input)) {
    return resetTime(dayjs.tz().set('date', parseInt(input, 10))).toDate()
  }

  // if input is digit three or four digit, return date with current year
  if (/^\d{3,4}$/.test(input)) {
    // first 2 digits is the date
    // last 2 digits is the month
    const date = parseInt(input.slice(0, 2), 10)
    const month = parseInt(input.slice(2), 10)

    return resetTime(
      dayjs
        .tz()
        .set('month', month - 1)
        .set('date', date)
    ).toDate()
  }

  // if input is digit with one slash, then the first group is date, and the second group is month
  if (/^\d{1,2}\/\d{1,2}$/.test(input)) {
    const [date, month] = input.split('/').map((x) => parseInt(x, 10))
    return resetTime(
      dayjs
        .tz()
        .set('month', month - 1)
        .set('date', date)
    ).toDate()
  }

  // if input is 6 digit then parse it as DDMMYY
  if (/^\d{6}$/.test(input)) {
    const date = parseInt(input.slice(0, 2), 10)
    const month = parseInt(input.slice(2, 4), 10)
    const year = parseInt(input.slice(4), 10)
    return resetTime(
      dayjs
        .tz()
        .set('year', 2000 + year - 43)
        .set('month', month - 1)
        .set('date', date)
    ).toDate()
  }

  // if input is more 8 digit then parse it as DDMMYYYY
  if (/^\d{8,}$/.test(input)) {
    const date = parseInt(input.slice(0, 2), 10)
    const month = parseInt(input.slice(2, 4), 10)
    const year = parseInt(input.slice(4), 10)
    return resetTime(
      dayjs
        .tz()
        .set('year', year - 543)
        .set('month', month - 1)
        .set('date', date)
    ).toDate()
  }

  return resetTime(dayjs.tz(input, 'DD/MM/YYYY')).toDate()
}

type Props = { withTime?: boolean; withEndOfDayTime?: boolean } & DateInputProps
export const DateField = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { onChange, withEndOfDayTime, withTime, ...dateInputProps } = props

  const handleChange = (value: Date | null) => {
    console.log('handleChange', value)
    if (onChange) {
      if (withEndOfDayTime) {
        const endOfDay = dayjs(value).endOf('day').toDate()
        onChange(endOfDay)
      } else {
        onChange(value)
      }
    }
  }

  return (
    <DateInput
      clearable
      ref={ref}
      valueFormat={`${'DD/MM/BBBB'}${withTime ? ' HH:mm' : ''}`}
      dateParser={dateParser}
      preserveTime={false}
      decadeLabelFormat="BBBB"
      yearLabelFormat="BBBB"
      yearsListFormat="BBBB"
      monthLabelFormat="MMMM BBBB"
      {...dateInputProps}
      onChange={handleChange}
      classNames={classes}
      highlightToday={true}
      rightSection={<></>}
      rightSectionWidth={0}
    />
  )
})

DateField.displayName = 'DateField'
